import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, User, MapPin, CalendarDays, ArrowRight, BadgeCheck, Trophy, XCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { tradeLabel, localizedSlug, money } from '@/data/constants';
import { PurchasedLead, LEAD_STAGES, METHOD_LABEL, formatLeadDate } from '@/lib/myLeads';
import { leadOutcomeChip } from '@/lib/contractorRatings';

const STAGE_TONE: Record<string, string> = {
  purchased: 'bg-blue-50 text-blue-700 ring-blue-200',
  contacted: 'bg-amber-50 text-amber-700 ring-amber-200',
  estimate: 'bg-violet-50 text-violet-700 ring-violet-200',
  won: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  lost: 'bg-slate-100 text-slate-500 ring-slate-200',
};

interface Props {
  lead: PurchasedLead;
  onStage?: (purchaseId: string, stage: string) => void;
  compact?: boolean;
}

const PurchasedLeadCard: React.FC<Props> = ({ lead, onStage, compact }) => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';
  const l = lead.listing;
  const c = lead.contact;
  const href = l ? path(`/jobs/${localizedSlug(l.slug, l.trade, lang)}`) : null;
  const method = METHOD_LABEL[lead.method] || METHOD_LABEL.unknown;
  const chip = leadOutcomeChip(l as any, profile?.id);

  return (
    <article className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
            <BadgeCheck className="h-3.5 w-3.5" /> {es ? 'Comprado' : 'Purchased'}
          </p>
          <h3 className="mt-1 line-clamp-2 text-base font-extrabold leading-snug text-slate-900">
            {l?.title || (es ? 'Proyecto comprado' : 'Purchased project')}
          </h3>
          {l && (
            <p className="mt-0.5 text-xs font-semibold text-slate-500">
              {tradeLabel(l.trade, lang)} · {[l.city, l.state].filter(Boolean).join(', ')}
            </p>
          )}
          {/* Homeowner-recorded outcome (Phase 1.1). Shown only once an outcome exists. */}
          {chip === 'hired' ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-bold text-white">
              <Trophy className="h-3 w-3" /> {es ? 'Contratado' : 'Hired'}
            </span>
          ) : chip === 'not_selected' ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200">
              <XCircle className="h-3 w-3" /> {es ? 'No seleccionado' : 'Not selected'}
            </span>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${
            STAGE_TONE[lead.stage] || STAGE_TONE.purchased
          }`}
        >
          {t(`dash.stage.${lead.stage}`)}
        </span>
      </div>

      <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" /> {formatLeadDate(lead.createdAt, lang)}
        </span>
        <span>{es ? method.es : method.en}</span>
        {lead.amount > 0 && <span>{money(lead.amount)}</span>}
      </p>

      {/* CONTACT — always visible, never behind another click. */}
      <div className="mt-4 space-y-2 rounded-xl bg-slate-50 p-3.5">
        {c ? (
          <>
            {c.contact_name && (
              <p className="flex items-center gap-2 text-sm font-bold text-slate-800">
                <User className="h-4 w-4 shrink-0 text-orange-500" /> {c.contact_name}
              </p>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-orange-600">
                <Phone className="h-4 w-4 shrink-0 text-orange-500" /> {c.phone}
              </a>
            )}
            {c.email && (
              <a href={`mailto:${c.email}`} className="flex items-center gap-2 break-all text-sm font-semibold text-slate-800 hover:text-orange-600">
                <Mail className="h-4 w-4 shrink-0 text-orange-500" /> {c.email}
              </a>
            )}
            {!compact && c.address && (
              <p className="flex items-start gap-2 text-sm text-slate-700">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" /> {c.address}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">
            {es
              ? 'Los datos de contacto están en la página del proyecto.'
              : 'Contact details are on the project page.'}
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        {href ? (
          <Link
            to={href}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800"
          >
            {es ? 'Ver detalles del lead' : 'View Lead Details'} <ArrowRight className="h-4 w-4" />
          </Link>
        ) : null}
        {c?.phone && (
          <a
            href={`tel:${c.phone}`}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border-2 border-emerald-500 px-4 text-sm font-bold text-emerald-700 hover:bg-emerald-50"
          >
            <Phone className="h-4 w-4" /> {es ? 'Llamar' : 'Call'}
          </a>
        )}
        {onStage && (
          <select
            value={lead.stage}
            onChange={(e) => onStage(lead.id, e.target.value)}
            aria-label={es ? 'Estado del lead' : 'Lead status'}
            className="min-h-[44px] rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800"
          >
            {LEAD_STAGES.map((s) => (
              <option key={s} value={s}>
                {t(`dash.stage.${s}`)}
              </option>
            ))}
          </select>
        )}
      </div>
    </article>
  );
};

export default PurchasedLeadCard;
